import { memo } from 'react';
import { ExternalLink } from 'lucide-react';
import { ExternalIdRow } from '../../types/apogee';
import { cn } from '../../utils/cn';

interface ExternalIdBadgeProps {
  externalId: ExternalIdRow;
  packSlug?: string;
  className?: string;
}

const SOURCE_CONFIG: Record<string, { label: string; color: string; urlFn: (id: string, slug?: string) => string }> = {
  curseforge: {
    label: 'CurseForge',
    color: 'bg-orange-500/15 text-orange-300 border-orange-500/25',
    urlFn: (id, slug) => slug
      ? `https://www.curseforge.com/minecraft/modpacks/${slug}`
      : `https://www.curseforge.com/minecraft/modpacks?search=${id}`,
  },
  modrinth: {
    label: 'Modrinth',
    color: 'bg-green-500/15 text-green-300 border-green-500/25',
    urlFn: (id) => `https://modrinth.com/modpack/${id}`,
  },
};

export const ExternalIdBadge = memo(({ externalId, packSlug, className }: ExternalIdBadgeProps) => {
  const config = SOURCE_CONFIG[externalId.source] || {
    label: externalId.source,
    color: 'bg-gray-500/15 text-gray-300 border-gray-500/25',
    urlFn: () => '#',
  };

  const url = config.urlFn(externalId.external_id, packSlug);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'badge hover:brightness-125 transition-[filter]',
        config.color,
        className,
      )}
      title={`Open on ${config.label}`}
    >
      <span>{config.label}</span>
      <span className="font-mono opacity-80 tabular-nums">{externalId.external_id}</span>
      <ExternalLink className="w-3 h-3 opacity-60" />
    </a>
  );
});

ExternalIdBadge.displayName = 'ExternalIdBadge';
